import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { markThreadPrivate } from '@/data/client';
import { SourceViewer } from '@/components/vault/SourceViewer';
import { Link } from 'react-router-dom';
import { Button, Tabs, Textarea } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { dateTime, relativeTime } from '@/lib/format';
import type { Activity } from '@/data/types';
import { FormError, Modal } from './Modal';

const ICON: Record<string, string> = {
  note: 'edit', created: 'plus', lifecycle_change: 'refresh-cw', lead: 'user-plus', meeting: 'calendar',
  vault: 'lock', stage_change: 'arrow-right', email: 'mail', call: 'phone', text: 'message-square', deal_created: 'dollar', lead_linked: 'link', lead_unlinked: 'x', meeting_cancelled: 'x',
  imported: 'upload', transcript: 'file-text', email_draft: 'send', prospect: 'target', prospect_status: 'refresh-cw',
};

/** What the prospect and the team actually said: email, transcripts, notes, meetings, calls and texts. */
const CONVERSATION = new Set(['email', 'transcript', 'note', 'meeting', 'call', 'text', 'email_draft']);

/* One timeline per account (build plan principle 3). Every activity type renders
   through here; new types only need an icon. "Conversations" hides the system
   events (stage changes, imports, links) to read the relationship itself. */
export function Timeline({ items, showContact, filterable }: { items: Activity[]; showContact?: boolean; filterable?: boolean }) {
  const [email, setEmail] = useState<Activity | null>(null);
  const [doc, setDoc] = useState<string | null>(null);
  const [view, setView] = useState<'all' | 'conversations'>('all');
  const shown = view === 'all' ? items : items.filter((a) => CONVERSATION.has(a.type));
  return (
    <>
    {filterable && items.length > 0 && (
      <div className="crm-timeline__filter">
        <Tabs items={[{ value: 'all', label: 'Everything' }, { value: 'conversations', label: 'Conversations', count: items.filter((a) => CONVERSATION.has(a.type)).length }]}
          value={view} onChange={(v) => setView(v as 'all' | 'conversations')} />
      </div>
    )}
    {!shown.length && <p className="lead-none">{view === 'all' ? 'Nothing here yet.' : 'No conversations yet — email, transcripts and notes show here.'}</p>}
    <ol className="crm-timeline">
      {shown.map((a) => (
        <li key={a.id} className={`crm-timeline__item crm-timeline__item--${a.type}`}>
          <span className="crm-timeline__icon"><Icon name={ICON[a.type] ?? 'info'} size={14} /></span>
          <div className="crm-timeline__main">
            <div className="crm-timeline__meta">
              <strong>{a.userName ?? a.authorLabel ?? (a.type === 'note' ? 'Unsigned note' : a.type === 'transcript' ? 'Transcript' : 'System')}</strong>
              {showContact && a.contactId && a.contactName && (
                <> on <Link to={`/contacts/${a.contactId}`}>{a.contactName}</Link></>
              )}
              <time dateTime={a.occurredAt} title={dateTime(a.occurredAt)}>{relativeTime(a.occurredAt)}</time>
            </div>
            {a.body && (a.type === 'email' && a.emailSourceId
              ? <button type="button" className="crm-timeline__body crm-linkbtn crm-timeline__email" onClick={() => setEmail(a)}>{a.body}</button>
              : a.sourceId
                ? <button type="button" className="crm-timeline__body crm-linkbtn crm-timeline__email" onClick={() => setDoc(a.sourceId)}>{a.body}</button>
                : <p className={a.type === 'note' ? 'crm-timeline__note' : 'crm-timeline__body'}>{a.body}</p>)}
          </div>
        </li>
      ))}
    </ol>
    {email && <EmailViewer item={email} onClose={() => setEmail(null)} />}
    {doc && <SourceViewer sourceId={doc} onClose={() => setDoc(null)} />}
    </>
  );
}

/* A captured conversation, read from its Client Vault text. "Mark private"
   removes it from clientIQ for good, including future replies. */
function EmailViewer({ item, onClose }: { item: Activity; onClose: () => void }) {
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState(false);
  const hide = useMutation({
    mutationFn: () => markThreadPrivate(item.emailThreadId!),
    onSuccess: () => { for (const k of ['company', 'contact', 'deal', 'vault', 'snapshot']) void qc.invalidateQueries({ queryKey: [k] }); onClose(); },
  });
  return (
    <>
      <SourceViewer sourceId={item.emailSourceId!} onClose={onClose}
        actions={<Button variant="ghost" iconLeft={<Icon name="lock" size={14} />} onClick={() => setConfirm(true)}>Mark private</Button>} />
      {confirm && (
        <Modal title="Mark this conversation private?" onClose={() => setConfirm(false)} footer={<>
          <Button variant="ghost" onClick={() => setConfirm(false)}>Cancel</Button>
          <Button variant="danger" onClick={() => hide.mutate()} disabled={hide.isPending}>Remove from clientIQ</Button>
        </>}>
          <p>The conversation, its attachments in the Client Vault and its timeline entry are removed for everyone, and later replies won't be captured. It stays in Gmail.</p>
          <FormError error={hide.error} />
        </Modal>
      )}
    </>
  );
}

export function NoteComposer({ onSave }: { onSave: (body: string) => Promise<unknown> }) {
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const save = async () => {
    if (!body.trim()) return;
    setSaving(true); setError(null);
    try { await onSave(body.trim()); setBody(''); } catch (e) { setError(e); } finally { setSaving(false); }
  };
  return (
    <div className="crm-composer">
      <Textarea
        placeholder="Add a note…"
        value={body}
        rows={3}
        onChange={(e) => setBody(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); void save(); } }}
      />
      <FormError error={error} />
      <div className="crm-composer__foot">
        <span>⌘ + Enter to save</span>
        <Button size="sm" onClick={save} disabled={saving || !body.trim()}>{saving ? 'Saving…' : 'Add note'}</Button>
      </div>
    </div>
  );
}
